import { useCallback, useMemo } from "react";
import { AppId, FCX, ID, SelectSingle, usePlatformContext } from "@datassential/platform-ui-lib";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import HomeSectionFooter from "@pages/Home/components/HomeSectionFooter";
import AccessLink from "@components/AccessLink/AccessLink";
import { PermissionLevelEnum } from "@models/PermissionSection";
import { requestTrialByFeature } from "@actions/page";
import { CategoryLensChainLtoRow, useHomeCategoryLensChainLtos } from "@apiHooks/useHomeCategoryLensChainLtos";
import useLocalStorageState from "@hooks/useLocalStorageState";
import HomeSectionLoader from "@pages/Home/components/HomeSectionLoader";
import { LR_APPLY_FILTERS_PATH, LR_CATEGORIES_IDS_PARAM, LR_CHAIN_NAME_PARAM } from "@pages/Home/constants";
import CategoryLensLtosTable from "./CategoryLensChainLtosTable";
import { KEY_METRIC_OPTIONS } from "./CategoryLensChainLtos.helpers";
import './CategoryLensChainLtos.scss';

interface CategoryLensChainLtosProps {
    categoryName: string;
    categoryId: ID;
    chainId: number;
    chainName: string;
    allCategoryIds: string;
}

const CategoryLensChainLtos: FCX<CategoryLensChainLtosProps> = (
    { 
        categoryName,
        categoryId,
        chainId,
        chainName,
        allCategoryIds,
    }
) => {
    const dispatch = useDispatch();
    const { userInfo: { products } } = usePlatformContext();
    const { data, isLoading } = useHomeCategoryLensChainLtos(chainId);
    const [activeMetricId, setActiveMetricId] = useLocalStorageState<keyof CategoryLensChainLtoRow>(
            'home-category-lens-chain-ltos',
            KEY_METRIC_OPTIONS[0].id,
        );

    const activeMetricTitle = useMemo(
        () => KEY_METRIC_OPTIONS.find(m => m.id === activeMetricId)?.title,
        [activeMetricId],
    );

    const tableData = useMemo(
        () => {
            if (!activeMetricTitle) return [];
            return data.rowsByCategory.get(categoryId ?? 0)?.get(activeMetricTitle) ?? [];
        },
        [data, categoryId, activeMetricTitle],
    );

    const launchesRatings = useMemo(
        () => products[AppId.LaunchesAndRatings],
        [products],
    );

    const conceptLocker = useMemo(
        () => products[AppId.ConceptLocker],
        [products],
    );

    const hasLaunchesRatingsAccess = useMemo(
        () => data.permissionLevel === PermissionLevelEnum.Full,
        [data],
    );

    const hasConceptLockerAccess = useMemo(
        () => !conceptLocker.isDisabled,
        [conceptLocker],
    );

    const launchesRatingsUrl = useMemo(
        () => {
            if (!launchesRatings.url) return '';

            const params = new URLSearchParams({
                [LR_CHAIN_NAME_PARAM]: chainName,
                [LR_CATEGORIES_IDS_PARAM]: categoryId ? String(categoryId) : allCategoryIds,
            });
            return `${launchesRatings.url}${LR_APPLY_FILTERS_PATH}?${params.toString()}`;
        },
        [launchesRatings.url, chainName, categoryId, allCategoryIds],
    );

    const requestLaunchesRatingsTrial = useCallback(() => {
        toast.info('Request has been sent');
        dispatch(requestTrialByFeature('scores'));
    }, [dispatch]);

    const requestConceptLockerTrial = useCallback(() => {
        toast.info('Request has been sent');
        dispatch(requestTrialByFeature('scores-cl'));
    }, [dispatch]);

    if (isLoading) return <HomeSectionLoader />;

    if (!tableData.length) return null;

    return (
        <div className="CategoryLensChainLtos HomeSection__content">
            <div className="CategoryLensChainLtos__header">
                <div className="CategoryLensChainLtos__header-left">
                    <span className="CategoryLensChainLtos__header-title">
                        Top performing&nbsp;
                        <span className="CategoryLensChainLtos__header-chain-name">{chainName}</span>
                        &nbsp;launches: {categoryName}
                    </span>
                    Jun 2014 - Present
                </div>
                <div className="CategoryLensChainLtos__header-right">
                    <span style={{ display: 'inline-block' }}>Based On</span>
                    <SelectSingle
                        className="HomeCategoryLens__select no-rem"
                        value={activeMetricId}
                        setValue={(id) => setActiveMetricId(id)}
                        options={KEY_METRIC_OPTIONS}
                        dropdownProps={{
                            place: 'bottom-start',
                        }}
                    />
                </div>
            </div>
            <CategoryLensLtosTable activeMetricId={activeMetricId} tableData={tableData} />
            <HomeSectionFooter style={{ marginTop: -20, gap: 30 }}>
                <AccessLink
                    url={conceptLocker.url || ''}
                    text="My Concept Locker"
                    altText=""
                    hasAccess={hasConceptLockerAccess}
                    onContactUsClick={requestConceptLockerTrial}
                    testId="linkHomeCategoryLensLtosMyConceptLocker"
                />
                <AccessLink
                    url={launchesRatingsUrl}
                    text={`More ${chainName} Launches`}
                    altText=""
                    hasAccess={hasLaunchesRatingsAccess}
                    onContactUsClick={requestLaunchesRatingsTrial}
                    testId="linkHomeCategoryLensLtosMoreChainLaunches"
                />
            </HomeSectionFooter>
        </div>
    );
};

export default CategoryLensChainLtos;